import { useEffect, useState } from "react";
import NotFound from "@/pages/NotFound";
import { toast } from "sonner";
import { Download, Search, Trash2, Users } from "lucide-react";
import { useAdmin } from "@/context/AdminContext";
import { api, errText } from "@/lib/api";

const when = (s) => { try { return new Date(s).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch (e) { return ""; } };

export default function SubscribersPage() {
  const { can, user } = useAdmin();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");

  const load = () => api.get("/subscribers").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errText(e)));
  useEffect(() => { if (can("mailing")) load(); }, [can]);

  if (user === null) return <div className="min-h-[60vh]" />;
  if (!can("mailing")) return <NotFound />;

  const shown = list.filter((s) => `${s.email} ${(s.name || "").toLowerCase()}`.includes(q.trim().toLowerCase()));
  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from the mailing list?`)) return;
    try { await api.delete(`/subscribers/${encodeURIComponent(email)}`); load(); toast.success("Removed"); } catch (e) { toast.error(errText(e)); }
  };
  const exportCsv = () => {
    const csv = "email,name,signed_up\n" + list.map((s) => `${s.email},"${(s.name || "").replace(/"/g, '""')}",${s.created_at || ""}`).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "cresway-mailing-list.csv"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  return (
    <div data-testid="subscribers-page" className="mx-auto max-w-5xl px-5 pb-28 pt-10 sm:px-8 lg:px-12">
      <p className="font-mono-label text-xs uppercase tracking-[0.25em] text-[#57534E]">Private · only you can see this</p>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-display text-5xl font-extrabold tracking-tight sm:text-6xl">Mailing list</h1>
        <button onClick={exportCsv} disabled={!list.length} data-testid="subscribers-export" className="flex items-center gap-2 bg-[#0A0A0A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#E62314] disabled:opacity-40">
          <Download className="h-4 w-4" />Download list
        </button>
      </div>
      <div className="mt-8 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3 bg-[#0A0A0A] px-5 py-4 text-white" data-testid="subscribers-count">
          <Users className="h-5 w-5 text-[#E62314]" /><span className="font-display text-3xl font-extrabold">{list.length}</span><span className="text-sm text-zinc-300">signed up</span>
        </div>
        <label className="flex min-w-[240px] flex-1 items-center gap-2 border border-black/20 bg-white px-4 py-3">
          <Search className="h-4 w-4 text-[#57534E]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search emails or names" className="w-full bg-transparent text-sm outline-none" data-testid="subscribers-search" />
        </label>
      </div>
      <div className="mt-6 border border-black/15 bg-white">
        {!shown.length ? <p className="p-10 text-center text-sm text-[#57534E]" data-testid="subscribers-empty">{list.length ? "No emails match your search." : "No one has signed up yet."}</p> : (
          <ul>
            {shown.map((s) => (
              <li key={s.email} className="flex items-center gap-4 border-b border-black/5 px-5 py-3 hover:bg-[#F3F2EF]" data-testid={`subscriber-${s.email}`}>
                <span className="min-w-0 flex-1 break-words">
                  {s.name ? <span className="block text-sm font-semibold" data-testid={`subscriber-name-${s.email}`}>{s.name}</span> : null}
                  <span className={`block text-sm ${s.name ? "font-normal text-[#57534E]" : "font-semibold"}`}>{s.email}</span>
                </span>
                <span className="hidden text-xs text-[#57534E] sm:inline">{when(s.created_at)}</span>
                <button onClick={() => remove(s.email)} aria-label="Remove" className="rounded p-1.5 hover:bg-black/10" data-testid={`subscriber-remove-${s.email}`}><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
